/**
 * DevUtilitiesButton - Floating Super Admin Utilities Button
 *
 * Floating button in bottom-right corner that provides access to dev utilities.
 * Only visible to super admins. Shows region indicator (green for dev, red for production)
 * and region-specific options.
 */

import { Box, Button, Menu } from '@chakra-ui/react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getRegionName, isDevRegion } from '~/services/dev-utilities/dev-utilities-service';
import { useAppStore } from '~/store';
import { DevUtilitiesMenu } from './DevUtilitiesMenu';

export const DevUtilitiesButton: React.FC = () => {
  const userInfo = useAppStore((s) => s.userInfo);
  const isSuperAdmin = userInfo?.isSuperAdmin ?? false;
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Parse projectId from URL path: /projects/:id/...
  const projectIdMatch = location.pathname.match(/^\/projects\/([^/]+)/);
  const projectId = projectIdMatch ? projectIdMatch[1] : undefined;

  const isDev = isDevRegion();
  const regionName = getRegionName();

  // Don't render if not super admin
  if (!isSuperAdmin) {
    return null;
  }

  return (
    <Box position="fixed" bottom={16} right={6} zIndex={9999}>
      <Menu.Root
        open={menuOpen}
        onOpenChange={(e) => setMenuOpen(e.open)}
        positioning={{ placement: 'top-end' }}
        lazyMount
        unmountOnExit
        closeOnSelect={true}
      >
        <Menu.Trigger asChild>
          <Button
            type="button"
            size="md"
            borderRadius="full"
            bg="white"
            shadow="lg"
            _hover={{ shadow: 'xl', transform: 'scale(1.05)' }}
            transition="all 0.2s"
            p={2}
          >
            <img
              src="/assets/avodah-logo.svg"
              alt="Avodah Logo"
              width={24}
              height={24}
              style={{ objectFit: 'contain' }}
            />
          </Button>
        </Menu.Trigger>
        <Menu.Positioner>
          <Menu.Content minW="280px" maxW="320px" display={menuOpen ? 'block' : 'none'}>
            <DevUtilitiesMenu isDev={isDev} regionName={regionName} projectId={projectId} />
          </Menu.Content>
        </Menu.Positioner>
      </Menu.Root>
    </Box>
  );
};
